// Typed errors for the two API responses the whole app handles the same way no
// matter which endpoint produced them: a permission denial (HTTP 403) and an
// expired/invalid session (HTTP 401). The fetch wrapper throws these from a
// single place (see fetch-wrapper.ts), the QueryClient's global cache handler
// turns them into a single toast (see client-config.ts + alert-context.tsx),
// and addErrorAlert suppresses the per-hook duplicates by message so they don't
// double-toast or get logged to Datadog as errors.

/** Shown when the API rejects an action the signed-in user isn't allowed to do (403). */
export const PERMISSION_DENIED_MESSAGE = "You don't have permission to perform this action.";

/** Shown when the session is gone and the user needs to sign in again (401). */
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

export class PermissionError extends Error {
  constructor(message: string = PERMISSION_DENIED_MESSAGE) {
    super(message);
    this.name = 'PermissionError';
  }
}

export class SessionExpiredError extends Error {
  constructor(message: string = SESSION_EXPIRED_MESSAGE) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/** True for the two errors the global cache handler presents on its own. */
export function isApiAuthError(error: unknown): error is PermissionError | SessionExpiredError {
  return error instanceof PermissionError || error instanceof SessionExpiredError;
}

/**
 * True when a free-form alert message is one the global cache handler already
 * surfaces. Hooks build messages like `Error creating order: ${err.message}`,
 * so we match by substring rather than equality. addErrorAlert uses this to
 * drop the per-hook duplicate (and skip the Datadog error it would otherwise log).
 */
export function isGloballyToastedMessage(message: string): boolean {
  return message.includes(PERMISSION_DENIED_MESSAGE) || message.includes(SESSION_EXPIRED_MESSAGE);
}

// Bridge between the QueryClient cache handlers (created at module load, outside
// React) and the alert context (a React provider). AlertContextProvider registers
// its toast callback on mount; the cache handlers call reportGlobalApiError.
type GlobalApiErrorHandler = (error: PermissionError | SessionExpiredError) => void;

let globalApiErrorHandler: GlobalApiErrorHandler | null = null;

export function registerGlobalApiErrorHandler(handler: GlobalApiErrorHandler | null): void {
  globalApiErrorHandler = handler;
}

/** Cache-level onError entry point: presents auth/permission errors, ignores the rest. */
export function reportGlobalApiError(error: unknown): void {
  if (isApiAuthError(error)) {
    globalApiErrorHandler?.(error);
  }
}
